import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ArrowRight, BookOpen, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import BookCard from "@/components/BookCard";
import NewsletterForm from "@/components/NewsletterForm";

const Home = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const response = await axios.get(`${API}/books/`);
        setBooks(response.data);
      } catch (error) {
        console.error("Failed to fetch books", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBooks();
  }, []);

  const featuredBooks = books.slice(0, 3);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-void" />
        <div className="hero-glow top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2" />
        <div className="hero-glow bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 opacity-20" />
        
        {/* Constellation Lines */}
        <div className="constellation-line w-64 top-1/3 left-0 rotate-12" />
        <div className="constellation-line w-48 bottom-1/3 right-0 -rotate-12" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 text-center">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-gold/20 bg-gold/5 mb-8 animate-fade-up opacity-0">
            <Sparkles size={14} className="text-gold" />
            <span className="text-gold text-xs uppercase tracking-widest font-body">Light Embodied</span>
          </div>
          
          {/* Main Heading */}
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl text-[#EAEAEA] leading-[0.95] mb-6 animate-fade-up opacity-0 stagger-1">
            What if nothing
            <br />
            <span className="text-gradient-gold">is broken?</span>
          </h1>
          
          {/* Subheading */}
          <p className="font-body text-lg md:text-xl text-[#A1A1AA] max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-up opacity-0 stagger-2">
            The overwhelm, the searching, the sense that something's off — 
            it's not a flaw. It's coherence trying to return. Somalume teaches you how to let it.
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up opacity-0 stagger-3">
            <Link to="/live">
              <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-10 uppercase text-xs tracking-widest btn-shine" data-testid="hero-sessions-btn">
                Join a Session
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/library">
              <Button variant="outline" className="border-white/20 text-[#EAEAEA] hover:bg-white/5 hover:border-gold/30 rounded-none h-14 px-10 uppercase text-xs tracking-widest" data-testid="hero-about-btn">
                Explore the Books
              </Button>
            </Link>
            <a 
              href="https://somalume.org/luma" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="hero-luma-btn"
            >
              <Button variant="outline" className="border-white/20 text-[#EAEAEA] hover:bg-white/5 hover:border-gold/30 rounded-none h-14 px-10 uppercase text-xs tracking-widest">
                Try Luma
              </Button>
            </a>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-up opacity-0 stagger-4">
          <div className="w-px h-12 bg-gradient-to-b from-gold/50 to-transparent" />
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 relative section-noise">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div>
              <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Core Insight</span>
              <h2 className="font-heading text-4xl md:text-5xl text-[#EAEAEA] mb-6">
                Healing is subtraction
              </h2>
              <div className="space-y-4 text-[#A1A1AA] leading-relaxed">
                <p>
                  Sensation is the primary event. Emotion, meaning, memory, identity — 
                  all are downstream interpretations of resonance.
                </p>
                <p>
                  Somalume doesn't add anything to you. It removes resistance so that 
                  coherence — your natural state — can return on its own.
                </p>
                <p>
                  No force. No striving. No becoming "better." 
                  Just biology remembering what it already knows.
                </p>
              </div>
              <Link to="/pillars" className="inline-flex items-center gap-2 text-gold mt-8 group" data-testid="philosophy-learn-more">
                <span className="text-sm uppercase tracking-widest">Explore the 8 Pillars</span>
                <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
            
            {/* Visual */}
            <div className="relative">
              <div className="aspect-square relative">
                <div className="absolute inset-0 border border-gold/10 rounded-sm" />
                <div className="absolute inset-4 border border-gold/20 rounded-sm" />
                <div className="absolute inset-8 border border-gold/30 rounded-sm flex items-center justify-center">
                  <div className="text-center p-8">
                    <img 
                      src="/somalume-logo.png" 
                      alt="Somalume" 
                      className="w-24 h-24 mx-auto mb-4 object-contain"
                    />
                    <span className="font-heading text-2xl text-[#EAEAEA] block mb-2">Coherence</span>
                    <span className="text-[#A1A1AA] text-sm">The organizing principle</span>
                  </div>
                </div>
              </div>
              <div className="glow-orb w-64 h-64 -bottom-16 -right-16" />
            </div>
          </div>
        </div>
      </section>

      {/* Start Here - Eight Pillars */}
      <section className="py-24 relative bg-gradient-to-b from-transparent via-gold/5 to-transparent">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">New to Somalume?</span>
          <h2 className="font-heading text-3xl md:text-4xl text-[#EAEAEA] mb-4">
            Start with the Eight Pillars
          </h2>
          <p className="text-[#A1A1AA] max-w-xl mx-auto mb-8 leading-relaxed">
            The essential map of human coherence. Eight structural laws that govern how energy becomes identity — and how identity becomes reality.
          </p>
          <p className="text-[#A1A1AA]/70 text-sm mb-8">
            Stillness · Movement · Rhythm · Geometry · Pressure · Coherence · Radiance · Recursion
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/book/eight-pillars">
              <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-12 px-8 uppercase text-xs tracking-widest">
                Get the Book — Free
                <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
            <Link to="/pillars">
              <Button variant="outline" className="border-white/20 text-[#EAEAEA] hover:bg-white/5 hover:border-gold/30 rounded-none h-12 px-8 uppercase text-xs tracking-widest">
                Read Free Preview
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Books */}
      <section className="py-24 bg-paper relative" data-testid="featured-books-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Library</span>
              <h2 className="font-heading text-4xl md:text-5xl text-[#EAEAEA]">
                Featured Books
              </h2>
            </div>
            <Link to="/library" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-gold mt-4 md:mt-0 group" data-testid="view-all-books">
              <span className="text-sm uppercase tracking-widest">View all books</span>
              <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[3/4] bg-subtle rounded-sm animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredBooks.map((book, index) => (
                <BookCard key={book.id} book={book} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Reading Path */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Path</span>
            <h2 className="font-heading text-4xl md:text-5xl text-[#EAEAEA] mb-4">
              Reading Paths
            </h2>
            <p className="text-[#A1A1AA] max-w-xl mx-auto">
              Nine volumes. One coherent system. Choose your entry point.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Entry */}
            <div className="p-8 border border-white/5 rounded-sm hover:border-green-500/30 transition-colors duration-300" data-testid="path-entry">
              <div className="w-12 h-12 rounded-sm bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-6">
                <span className="font-heading text-green-400 text-xl">1</span>
              </div>
              <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">Foundation</h3>
              <p className="text-[#A1A1AA] text-sm mb-4">
                Start with Cosmic Anatomy Vol. 1 — the foundation of everything.
              </p>
              <Link to="/book/cosmic-anatomy-vol-1" className="text-green-400 text-sm uppercase tracking-widest inline-flex items-center gap-1 group">
                Begin here <ArrowRight size={14} className="transform group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            {/* Transform */}
            <div className="p-8 border border-white/5 rounded-sm hover:border-blue-500/30 transition-colors duration-300" data-testid="path-transform">
              <div className="w-12 h-12 rounded-sm bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-6">
                <span className="font-heading text-blue-400 text-xl">2</span>
              </div>
              <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">Practice</h3>
              <p className="text-[#A1A1AA] text-sm mb-4">
                The Seven Gates offers practical protocols for coherence.
              </p>
              <Link to="/book/seven-gates" className="text-blue-400 text-sm uppercase tracking-widest inline-flex items-center gap-1 group">
                Explore protocols <ArrowRight size={14} className="transform group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            {/* Deep */}
            <div className="p-8 border border-white/5 rounded-sm hover:border-purple-500/30 transition-colors duration-300" data-testid="path-deep">
              <div className="w-12 h-12 rounded-sm bg-purple-500/10 border border-purple-500/30 flex items-center justify-center mb-6">
                <span className="font-heading text-purple-400 text-xl">3</span>
              </div>
              <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">Deep Theory</h3>
              <p className="text-[#A1A1AA] text-sm mb-4">
                The Aetheric Periodic Table maps the physics of consciousness.
              </p>
              <Link to="/book/aetheric-periodic-table-vol-1" className="text-purple-400 text-sm uppercase tracking-widest inline-flex items-center gap-1 group">
                Explore depths <ArrowRight size={14} className="transform group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Luma Section */}
      <section className="py-16 relative border-t border-white/5">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">Experience It</span>
          <h2 className="font-heading text-2xl md:text-3xl text-[#EAEAEA] mb-3">
            Check in with your nervous system
          </h2>
          <p className="text-[#A1A1AA] text-sm mb-6">
            A 2-minute guided reset to notice where you are right now.
          </p>
          <a 
            href="https://somalume.org/luma" 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="section-luma-btn"
          >
            <Button className="bg-gold/90 text-[#050505] hover:bg-gold rounded-full h-12 px-8 uppercase text-xs tracking-widest">
              Try Luma
            </Button>
          </a>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 bg-paper relative">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <NewsletterForm />
        </div>
      </section>
    </div>
  );
};

export default Home;
